import { useEffect } from 'react'
import { createLogger } from '@/lib/logger'
import { desktopSecureStorage, isTauri } from '@/lib/utils/tauri'
import { resetPersistedModuleFilters } from '@/store/filter-scope'

import { SessionChangedError } from '@scrambl/shared/session-scope'

const logger = createLogger('hooks/useDesktopAccountTransition')

export const DESKTOP_ACCOUNT_TRANSITION_EVENT =
  'desktop-account-transition' as const

export interface DesktopAccountTransitionPayload {
  destination: '/organizations' | '/login?addAccount=true' | '/login'
  credentialSessionKey: string | null
}

/**
 * Receives account changes made in the dedicated Settings webview and reloads
 * the main webview against the newly selected desktop credentials.
 */
export function useDesktopAccountTransition(): void {
  useEffect(() => {
    if (!isTauri()) return

    let cancelled = false
    let revision = 0
    let unlisten: (() => void) | undefined

    void import('@tauri-apps/api/event')
      .then(({ listen }) =>
        listen<DesktopAccountTransitionPayload>(
          DESKTOP_ACCOUNT_TRANSITION_EVENT,
          event => {
            const destination = event.payload?.destination
            if (
              destination !== '/organizations' &&
              destination !== '/login?addAccount=true' &&
              destination !== '/login'
            ) {
              logger.warn('Ignoring invalid desktop account destination')
              return
            }

            const key = event.payload?.credentialSessionKey
            if (key !== null && typeof key !== 'string') return
            const current = ++revision
            void (async () => {
              const session = await desktopSecureStorage.captureSession()
              if (
                cancelled ||
                revision !== current ||
                session.credentialSessionKey !== key
              )
                return
              session.assertCurrent()
              resetPersistedModuleFilters()
              localStorage.removeItem('currentOrganizationId')
              window.location.href = destination
            })().catch(error => {
              if (!(error instanceof SessionChangedError))
                logger.warn('Desktop account transition could not be verified')
            })
          },
        ),
      )
      .then(listener => {
        if (cancelled) {
          listener()
        } else {
          unlisten = listener
        }
      })
      .catch(error => {
        logger.error('Failed to listen for desktop account changes:', error)
      })

    return () => {
      cancelled = true
      unlisten?.()
    }
  }, [])
}
